import type { APIResponse } from "../types/common";
import type { Server } from "../types/server";

import { processError } from "../utils";

export type CreateServerOptions = {
  name: string;
  slug: string;
  description: string;
  tags: string[];
  contentRating: string;
  gameMode: string;
  hostingType: string;
  gameVersion: string;
  mapId: string;
  ipAddress: string;
  port: number;
  maxPlayers: number;
  packageVersionIds: string[];
  previewKeys?: string[];
  thumbnailKeys?: string[];
};
export async function createServer(
  bearerToken: string,
  options: CreateServerOptions
): Promise<APIResponse<Server>> {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_BASE}/api/v1/servers`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${bearerToken}`,
        },
        body: JSON.stringify(options),
      }
    );

    if (response.status === 200) {
      const data = await response.json();
      return { success: true, data };
    }

    let error = "Unknown error";
    switch (response.status) {
      case 400:
        error = "Invalid input";
        break;
      case 401:
        error = "Logged out";
        break;
      case 404:
        error = "Map not found";
        break;
      case 409:
        error = "Server slug taken";
    }

    return { success: false, error };
  } catch (error) {
    console.error("Error creating server:", error);
    return { success: false, error: processError(error) };
  }
}

export async function getPublicServers(
  bearerToken: string,
  {
    page = 1,
    limit = 16,
  }: {
    page?: number;
    limit?: number;
  }
): Promise<APIResponse<Server[]>> {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    const response = await fetch(
      `${process.env.REACT_APP_API_BASE}/api/v1/servers?${params.toString()}`,
      {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${bearerToken}`,
        },
      }
    );

    if (response.status !== 200)
      return {
        success: false,
        error: "Logged out",
      };

    const data = await response.json();
    return { success: true, data };
  } catch (error) {
    console.error("Error getting servers:", error);
    return { success: false, error: processError(error) };
  }
}

export async function getOwnedServers(
  bearerToken: string,
  {
    page = 1,
    limit = 16,
  }: {
    page?: number;
    limit?: number;
  }
): Promise<APIResponse<Server[]>> {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    const response = await fetch(
      `${
        process.env.REACT_APP_API_BASE
      }/api/v1/servers/me?${params.toString()}`,
      {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${bearerToken}`,
        },
      }
    );

    if (response.status !== 200)
      return {
        success: false,
        error: "Logged out",
      };

    const data = await response.json();
    return { success: true, data };
  } catch (error) {
    console.error("Error getting owned servers:", error);
    return { success: false, error: processError(error) };
  }
}

export async function getServerById(
  bearerToken: string,
  serverId: string
): Promise<APIResponse<Server>> {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_BASE}/api/v1/servers/${serverId}`,
      {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${bearerToken}`,
        },
      }
    );

    if (response.status !== 200)
      return {
        success: false,
        error: response.status === 404 ? "Server not found" : "Logged out",
      };

    const data = await response.json();
    return { success: true, data };
  } catch (error) {
    console.error("Error getting server:", error);
    return { success: false, error: processError(error) };
  }
}
